import { useEffect, useRef, useState } from 'react'
import { Helmet } from 'react-helmet-async'

import { Check, ChevronLeft } from 'lucide-react'

import { track } from '@/api/tracking'
import { ChromaKeyVideo } from '@/components/food-cost/ChromaKeyVideo'
import { ASSETS, CHROMA_KEY_SETTINGS, OUTBOUND_TRACK_OPTIONS } from '@/components/food-cost/constants'
import { cn } from '@/lib/utils'
import { SITE_ORIGIN } from '@/navigation/constants'
import { PageHelmet } from '@/shared/ui/PageHelmet'
import '@/styles/food-cost-fonts.css'

import { DesktopSideCards, MobileComposition } from './RouterMocks'
import { ROUTER_QUESTIONS, buildResultUrl, resolveRecommendation } from './quiz'

const ASSET_BASE = '/router-landing'
const TOTAL_STEPS = ROUTER_QUESTIONS.length
const DESKTOP_MIN_WIDTH = 1100
const DESKTOP_MIN_HEIGHT = 620
/** Ignore taps right after an answer so a double tap can't answer the next question too. */
const PICK_GUARD_MS = 250
/** Max wait for the tracking callback before navigating anyway (blocked tracker, slow network). */
const NAVIGATE_FALLBACK_MS = 500
/** Meta pixel has no callback: give it a moment to flush before leaving the page. */
const META_PIXEL_FLUSH_MS = 200
const CHOOSE_ONE_LABEL = 'Elige una opción'
const ANALYZING_MS = 3000
const ANALYZING_STEPS = [
    'Revisando tus respuestas',
    'Comparando con negocios como el tuyo',
    'Preparando tu recomendación',
] as const

const TITLE = 'Aumenta lo que ganas en tu negocio | Tipi'
const DESCRIPTION =
    'Responde 3 preguntas y te mostramos la solución de Tipi que mejor se adapta a tu negocio de hostelería.'

const CAPS_MUTED = 'font-bold uppercase tracking-[0.14em] text-tipi-muted'
const CARD_SHADOW = 'shadow-[0_2px_10px_oklch(0%_0_0/0.25)]'

type Viewport = { w: number; h: number }

function useViewport(): Viewport | null {
    // Read the real size on the first render so desktop never flashes the mobile layout.
    const [viewport, setViewport] = useState<Viewport | null>(() =>
        typeof window === 'undefined' ? null : { w: window.innerWidth, h: window.innerHeight },
    )

    useEffect(() => {
        const update = () => setViewport({ w: window.innerWidth, h: window.innerHeight })
        update()
        window.addEventListener('resize', update)
        return () => window.removeEventListener('resize', update)
    }, [])

    return viewport
}

function Brand({ small }: { small?: boolean }) {
    return (
        <div className={cn('flex items-center', small ? 'gap-2.5' : 'gap-3')}>
            <div className={cn('shrink-0 overflow-hidden', small ? 'h-[30px] w-[29px]' : 'h-10 w-[38px]')}>
                <img
                    src={`${ASSET_BASE}/tipi-logo-cream.png`}
                    alt='Tipi'
                    className={cn(
                        'block max-w-none',
                        small ? '-ml-2 -mt-[5px] size-[45px]' : '-ml-[11px] -mt-1.5 size-[60px]',
                    )}
                />
            </div>
            <span className={cn(CAPS_MUTED, small ? 'text-[11px]' : 'text-xs')}>Para pequeños hosteleros</span>
        </div>
    )
}

function Headline({ className }: { className?: string }) {
    return (
        <h1 className={cn('m-0 font-extrabold tracking-[-0.04em] [text-wrap:balance]', className)}>
            <span className='text-[1.18em]'>Aumenta</span> <span className='text-tipi-flame'>lo que ganas</span> en tu
            negocio
        </h1>
    )
}

function Progress({ step, compact }: { step: number; compact?: boolean }) {
    return (
        <div className='flex gap-1'>
            {ROUTER_QUESTIONS.map((question, index) => (
                <div
                    key={question.id}
                    className={cn(
                        'h-1 rounded-full transition-colors duration-300',
                        compact ? 'w-5' : 'w-[22px]',
                        index < step ? 'bg-tipi-flame' : index === step ? 'bg-tipi-ember' : 'bg-tipi-border',
                    )}
                />
            ))}
        </div>
    )
}

function RadioMark({ selected }: { selected: boolean }) {
    return (
        <span
            aria-hidden
            className={cn(
                'flex size-[22px] shrink-0 items-center justify-center rounded-full border-2',
                selected ? 'border-tipi-flame' : 'border-tipi-outline',
            )}
        >
            {selected ? <span className='size-2.5 rounded-full bg-tipi-flame' /> : null}
        </span>
    )
}

function AnalyzingPanel({ compact }: { compact?: boolean }) {
    const [filling, setFilling] = useState(false)
    const [checked, setChecked] = useState(0)

    useEffect(() => {
        const raf = window.requestAnimationFrame(() => setFilling(true))
        const timers = ANALYZING_STEPS.map((_, index) =>
            window.setTimeout(() => setChecked(index + 1), (ANALYZING_MS * (index + 1)) / ANALYZING_STEPS.length - 250),
        )
        return () => {
            window.cancelAnimationFrame(raf)
            timers.forEach(window.clearTimeout)
        }
    }, [])

    return (
        <div
            role='status'
            aria-live='polite'
            className={cn(
                'flex flex-col justify-center border border-tipi-border bg-tipi-surface text-left',
                CARD_SHADOW,
                compact ? 'h-full gap-3 rounded-3xl p-4' : 'mt-2 w-[min(100%,500px)] gap-4 rounded-[28px] p-6',
            )}
        >
            <h2
                className={cn(
                    'm-0 font-extrabold leading-[1.05] tracking-[-0.03em] [text-wrap:balance]',
                    compact ? 'text-[22px]' : 'text-[28px]',
                )}
            >
                Analizando la mejor opción para tu negocio
            </h2>
            <div className='h-1.5 overflow-hidden rounded-full bg-tipi-border'>
                <div
                    className='h-full rounded-full bg-[linear-gradient(90deg,var(--tipi-ember),var(--tipi-flame))] transition-[width] ease-in-out'
                    style={{ width: filling ? '100%' : '0%', transitionDuration: `${ANALYZING_MS}ms` }}
                />
            </div>
            <ul className={cn('m-0 flex list-none flex-col p-0', compact ? 'gap-1.5' : 'gap-2')}>
                {ANALYZING_STEPS.map((label, index) => {
                    const isDone = index < checked
                    return (
                        <li
                            key={label}
                            className={cn(
                                'flex items-center gap-2.5 font-semibold transition-colors duration-300',
                                compact ? 'text-[13px]' : 'text-sm',
                                isDone ? 'text-tipi-fg' : 'text-tipi-muted',
                            )}
                        >
                            <span
                                className={cn(
                                    'flex size-[18px] shrink-0 items-center justify-center rounded-full transition-colors duration-300',
                                    isDone
                                        ? 'bg-tipi-green text-tipi-bg'
                                        : 'border-[1.5px] border-dashed border-tipi-muted',
                                )}
                            >
                                {isDone ? <Check size={11} strokeWidth={4} /> : null}
                            </span>
                            {label}
                        </li>
                    )
                })}
            </ul>
        </div>
    )
}

function redirectToResult(answers: number[]) {
    const recommendation = resolveRecommendation(answers)
    const profile = recommendation?.profile
    const plan = recommendation?.plan
    const url = buildResultUrl(answers, window.location.search)

    let navigated = false
    let trackingSent = false
    let pixelFlushed = false

    // Safety net: never trap the visitor if the tracker is blocked or slow.
    const fallbackTimer = window.setTimeout(() => navigate(), NAVIGATE_FALLBACK_MS)

    const navigate = () => {
        if (navigated) return
        navigated = true
        window.clearTimeout(fallbackTimer)
        window.location.assign(url)
    }
    const navigateWhenSent = () => {
        if (trackingSent && pixelFlushed) navigate()
    }

    try {
        const fbq = (window as unknown as { fbq?: (...args: unknown[]) => void }).fbq
        if (typeof fbq === 'function') {
            fbq('trackCustom', 'QuizCompleted', { perfil: profile, plan })
            window.setTimeout(() => {
                pixelFlushed = true
                navigateWhenSent()
            }, META_PIXEL_FLUSH_MS)
        } else {
            pixelFlushed = true
        }
    } catch {
        // Meta pixel is optional and consent-gated.
        pixelFlushed = true
    }

    // Without measurement consent `track` calls the callback immediately.
    track(
        'Router Quiz Completed',
        {
            perfil: profile,
            plan,
            answers: answers.map(answer => answer + 1),
            from: new URLSearchParams(window.location.search).get('from') ?? undefined,
        },
        OUTBOUND_TRACK_OPTIONS,
        () => {
            trackingSent = true
            navigateWhenSent()
        },
    )
}

export function RouterLandingPage() {
    const viewport = useViewport()
    const [step, setStep] = useState(0)
    const [answers, setAnswers] = useState<number[]>([])

    const done = step >= TOTAL_STEPS
    const question = ROUTER_QUESTIONS[Math.min(step, TOTAL_STEPS - 1)]

    const w = viewport?.w ?? 390
    const h = viewport?.h ?? 720
    const isDesktop = w >= DESKTOP_MIN_WIDTH && h >= DESKTOP_MIN_HEIGHT
    const sideScale = Math.max(0.6, Math.min(1, (h - 40) / 800, (w - 680) / 2 / 360))
    const tall = h >= 800
    const sheetHeight = tall ? 330 : h >= 680 ? 290 : 262
    const mobileStep = Math.min(step, 2) as 0 | 1 | 2
    const compositionScale = Math.min(1.15, (w - 32) / 340, Math.max(0, h - sheetHeight - 190) / 260)
    const showComposition = compositionScale * 260 >= 110

    const stepLabel = `${Math.min(step + 1, TOTAL_STEPS)} de ${TOTAL_STEPS}`
    const subline = `Responde ${TOTAL_STEPS} preguntas y te mostramos solo la solución que mejor se adapta a tu negocio.`

    const lastPickAt = useRef(0)

    const pick = (optionIndex: number) => {
        const now = performance.now()
        if (done || now - lastPickAt.current < PICK_GUARD_MS) return
        lastPickAt.current = now
        setAnswers(current => [...current.slice(0, step), optionIndex])
        setStep(current => current + 1)
    }

    const goBack = () => setStep(current => Math.max(0, current - 1))

    useEffect(() => {
        if (!done) return
        const timer = window.setTimeout(() => redirectToResult(answers), ANALYZING_MS)
        return () => window.clearTimeout(timer)
    }, [done, answers])

    const selectedOption = answers[step]
    const optionState = (optionIndex: number) =>
        optionIndex === selectedOption
            ? 'border-tipi-flame bg-tipi-flame-dim'
            : 'border-tipi-outline bg-transparent hover:border-tipi-flame'

    const optionButtonBase =
        'cursor-pointer border-[1.5px] text-left transition-[background,border-color,transform] duration-150 active:scale-[0.98]'

    return (
        <div className='relative h-dvh overflow-hidden bg-tipi-bg font-archivo text-tipi-fg antialiased [-webkit-tap-highlight-color:transparent] [background:radial-gradient(70%_55%_at_50%_30%,oklch(22%_0.025_45)_0%,var(--tipi-bg)_70%)]'>
            <PageHelmet locale='es'>
                <title>{TITLE}</title>
                <meta name='description' content={DESCRIPTION} />
                <meta property='og:type' content='website' />
                <meta property='og:title' content={TITLE} />
                <meta property='og:description' content={DESCRIPTION} />
                <link rel='canonical' href={`${SITE_ORIGIN}/empieza`} />
            </PageHelmet>
            <Helmet>
                <meta name='theme-color' content='#1c1613' />
            </Helmet>

            {isDesktop ? (
                <>
                    <DesktopSideCards scale={sideScale} />
                    <div className='relative z-[1] mx-auto flex h-full max-w-[640px] flex-col items-center justify-center gap-[clamp(10px,2vh,18px)] p-6 text-center'>
                        <ChromaKeyVideo
                            src={ASSETS.heroVideoSource}
                            poster={ASSETS.heroVideoPoster}
                            settings={CHROMA_KEY_SETTINGS}
                            aria-label='Animación del personaje Tipi'
                            className='w-[clamp(200px,30vh,300px)] shrink-0'
                        />
                        <Brand />
                        <Headline className='text-[clamp(40px,5.2vw,72px)] leading-[0.96]' />
                        <p className='m-0 max-w-[460px] text-[17px] leading-[1.45] text-tipi-muted [text-wrap:pretty]'>
                            {subline}
                        </p>

                        {done ? (
                            <AnalyzingPanel />
                        ) : (
                            <div className='mt-2 flex w-full flex-col items-center gap-3'>
                                <div className='flex items-center gap-3'>
                                    <Progress step={step} />
                                    <span className={cn('text-[11px] tabular-nums', CAPS_MUTED, 'font-semibold')}>
                                        {stepLabel}
                                    </span>
                                </div>
                                <h2 className='m-0 text-2xl font-bold leading-[1.15] tracking-[-0.02em] [text-wrap:balance]'>
                                    {question.title}
                                </h2>
                                {question.hint ? (
                                    <p className='m-0 -mt-1 text-xs text-tipi-muted'>{question.hint}</p>
                                ) : null}
                                <p className='m-0 -mt-1 text-xs font-bold text-tipi-flame'>{CHOOSE_ONE_LABEL}</p>
                                <div
                                    role='radiogroup'
                                    aria-label={question.title}
                                    className='grid w-full grid-cols-3 gap-3'
                                >
                                    {question.options.map((option, optionIndex) => (
                                        <button
                                            key={option.label}
                                            type='button'
                                            role='radio'
                                            aria-checked={optionIndex === selectedOption}
                                            onClick={() => pick(optionIndex)}
                                            className={cn(
                                                optionButtonBase,
                                                optionState(optionIndex),
                                                'flex min-h-11 flex-col items-start gap-3.5 rounded-[10px] p-4',
                                            )}
                                        >
                                            <RadioMark selected={optionIndex === selectedOption} />
                                            <span className='flex flex-col gap-1'>
                                                <span className='text-base font-bold leading-[1.25]'>
                                                    {option.label}
                                                </span>
                                                <span className='text-xs leading-[1.3] text-tipi-muted'>
                                                    {option.meta}
                                                </span>
                                            </span>
                                        </button>
                                    ))}
                                </div>
                                <div className='flex min-h-10 items-center justify-center'>
                                    {step > 0 ? (
                                        <button
                                            type='button'
                                            onClick={goBack}
                                            className='min-h-10 cursor-pointer px-3 text-sm font-semibold text-tipi-muted transition-colors hover:text-tipi-fg'
                                        >
                                            Atrás
                                        </button>
                                    ) : null}
                                </div>
                            </div>
                        )}
                    </div>
                </>
            ) : (
                <div className='relative z-[1] mx-auto flex h-full max-w-[460px] flex-col gap-[clamp(10px,1.8vh,16px)] px-4 pb-[max(14px,env(safe-area-inset-bottom))] pt-[max(14px,env(safe-area-inset-top))]'>
                    <div className='relative flex min-h-0 flex-1 basis-0 items-center justify-center'>
                        {showComposition ? <MobileComposition step={mobileStep} scale={compositionScale} /> : null}
                    </div>

                    <div className='flex shrink-0 flex-col items-start gap-[clamp(6px,1.2vh,10px)]'>
                        <Brand small />
                        <Headline className='text-[clamp(28px,min(9.4vw,5.4vh),48px)] leading-[0.98]' />
                        {step === 0 && tall ? (
                            <p className='m-0 text-sm leading-[1.4] text-tipi-muted [text-wrap:pretty]'>{subline}</p>
                        ) : null}
                    </div>

                    {done ? (
                        <div className='shrink-0' style={{ height: sheetHeight }}>
                            <AnalyzingPanel compact />
                        </div>
                    ) : (
                        <div
                            className={cn(
                                'flex shrink-0 flex-col gap-1.5 rounded-3xl border border-tipi-border bg-tipi-surface p-3',
                                CARD_SHADOW,
                            )}
                            style={{ height: sheetHeight }}
                        >
                            <div className='flex items-center justify-between gap-3 px-1'>
                                <Progress step={step} compact />
                                <span className={cn('text-[10px] tabular-nums', CAPS_MUTED, 'font-semibold')}>
                                    {stepLabel}
                                </span>
                            </div>
                            <h2 className='mx-1 mb-0.5 mt-0 overflow-hidden text-ellipsis whitespace-nowrap text-[clamp(14px,4.6vw,17px)] font-bold leading-tight tracking-[-0.02em]'>
                                {question.title}
                            </h2>
                            {question.hint ? (
                                <p className='m-0 mx-1 -mt-1 text-[11px] leading-tight text-tipi-muted'>
                                    {question.hint}
                                </p>
                            ) : null}
                            <p className='m-0 mx-1 -mt-1 text-[11px] font-bold leading-tight text-tipi-flame'>
                                {CHOOSE_ONE_LABEL}
                            </p>
                            {question.options.map((option, optionIndex) => (
                                <button
                                    key={option.label}
                                    type='button'
                                    role='radio'
                                    aria-checked={optionIndex === selectedOption}
                                    onClick={() => pick(optionIndex)}
                                    className={cn(
                                        optionButtonBase,
                                        optionState(optionIndex),
                                        'flex min-h-11 flex-1 basis-0 items-center gap-3 rounded-xl px-3.5 py-1.5',
                                    )}
                                >
                                    <RadioMark selected={optionIndex === selectedOption} />
                                    <span className='flex min-w-0 flex-1 flex-col gap-0.5'>
                                        <span className='text-sm font-bold leading-[1.2]'>{option.label}</span>
                                        {tall ? (
                                            <span className='text-[11px] leading-tight text-tipi-muted'>
                                                {option.meta}
                                            </span>
                                        ) : null}
                                    </span>
                                </button>
                            ))}
                            <div className='flex h-[26px] shrink-0 items-center px-1'>
                                {step > 0 ? (
                                    <button
                                        type='button'
                                        onClick={goBack}
                                        className='flex min-h-8 cursor-pointer items-center gap-1 text-[13px] font-semibold text-tipi-muted hover:text-tipi-fg'
                                    >
                                        <ChevronLeft size={14} strokeWidth={2.5} />
                                        Atrás
                                    </button>
                                ) : null}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}
